import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, resolveImg } from "@/App";
import Seo from "@/components/Seo";

const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function formatDate(dateStr) {
  if (!dateStr) return "";
  // Try ISO format first
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return {
      day: d.getDate(),
      month: MONTHS_ES[d.getMonth()],
      year: d.getFullYear(),
      weekday: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][d.getDay()],
    };
  }
  return { raw: dateStr };
}

export default function Upcoming() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/upcoming`).then((r) => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Filter: only future or no date filter, sort by date
  const now = new Date();
  const futureItems = items.filter((i) => {
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(i.date);
    if (!iso) return true;
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return d >= now || d.toDateString() === now.toDateString();
  });

  return (
    <div data-testid="upcoming-page">
      <Seo
        title="Próximas tertulias"
        description="Calendario de próximos encuentros de la Tertulia In Dubio: invitados de honor, fechas y temas. Únete al debate sevillano sobre los problemas sociales contemporáneos."
        path="/proximas"
      />
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-10">
        <span className="eyebrow"><span className="decor-line"></span>Calendario · Próximos encuentros</span>
        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-10 items-end mt-4">
          <h1 className="font-serif text-[11vw] md:text-[96px] lg:text-[120px] leading-[0.88] tracking-tight font-light">
            Próximas<br/>tertulias.
          </h1>
          <p className="prose-bio max-w-md">
            Quiénes visitarán nuestra mesa en los próximos encuentros. Cada tertulia es una invitación abierta a escuchar, pensar y dudar.
          </p>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10"><div className="hairline"></div></div>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        {loading ? (
          <div className="space-y-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-40 bg-[rgba(18,18,18,0.05)] animate-pulse"></div>
            ))}
          </div>
        ) : futureItems.length === 0 ? (
          <div className="text-center py-20 max-w-xl mx-auto" data-testid="no-upcoming">
            <span className="eyebrow">Estamos preparando el próximo encuentro</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              No hay tertulias anunciadas por el momento.
            </h2>
            <p className="prose-bio mt-5">
              Vuelve pronto para conocer al próximo invitado. Mientras tanto, puedes recorrer el <Link to="/invitados" className="underline decoration-[var(--gold)] underline-offset-4">archivo de invitados</Link> o las <Link to="/aniversario" className="underline decoration-[var(--gold)] underline-offset-4">celebraciones pasadas</Link>.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {futureItems.map((item, i) => {
              const d = formatDate(item.date);
              return (
                <article
                  key={item.id}
                  className="glass p-6 md:p-8 grid md:grid-cols-[180px_1fr] gap-6 anim-fadeup"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  data-testid={`upcoming-${item.id}`}
                >
                  {/* Date block */}
                  <div className="text-center md:border-r border-[var(--line)] md:pr-6 flex flex-col justify-center">
                    {d.raw ? (
                      <div className="font-serif text-2xl text-[var(--gold-deep)]">{d.raw}</div>
                    ) : (
                      <>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] block mb-1">{d.weekday}</span>
                        <div className="font-serif text-[72px] leading-none text-[var(--text-ink)]">{d.day}</div>
                        <div className="font-serif text-lg text-[var(--gold-deep)] capitalize">{d.month}</div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-1">{d.year}</div>
                      </>
                    )}
                    {item.time && (
                      <div className="mt-3 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                        {item.time}
                      </div>
                    )}
                    {item.status === "tentative" && (
                      <span className="inline-block mt-3 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] bg-[rgba(201,167,74,0.15)] text-[var(--gold-deep)]">
                        Por confirmar
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex gap-5">
                    {item.image && (
                      <div className="w-24 md:w-32 aspect-[4/5] overflow-hidden border border-[var(--line)] flex-shrink-0 portrait-frame">
                        <img src={resolveImg(item.image)} alt={item.guest_name} style={{filter:"saturate(0.9)"}} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="eyebrow">Invitado</span>
                      <h2 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">
                        {item.guest_name}
                      </h2>
                      {item.guest_role && (
                        <p className="text-[14px] text-[var(--sky-deep)] italic mt-2 leading-snug">{item.guest_role}</p>
                      )}
                      {item.location && (
                        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-3">
                          📍 {item.location}
                        </p>
                      )}
                      {item.description && (
                        <p className="prose-bio mt-4 text-[14px]">{item.description}</p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
